export interface ConsentTextSegment {
  type: 'text' | 'link';
  value: string;
  url?: string;
}

const DEFAULT_CONSENT_LABEL = 'terms & conditions';

/** Default consent sentence with the label wrapped in `{curly braces}`. */
export function buildDefaultConsentText(label: string = DEFAULT_CONSENT_LABEL): string {
  const token = label.trim() ? `{${label.trim()}}` : `{${DEFAULT_CONSENT_LABEL}}`;
  return `By continuing, you agree to our ${token} and consent to receive an SMS.`;
}

/** Ensures stored consent text uses `{label}` when the label appears as plain text. */
export function normalizeConsentTextWithLabel(consentText: string, consentLabel: string): string {
  const text = consentText.trim();
  const label = consentLabel.trim();
  if (!text || !label) {
    return consentText;
  }

  const token = `{${label}}`;
  if (text.includes(token)) {
    return consentText;
  }

  if (text.includes(label)) {
    return text.replace(label, token);
  }

  return consentText;
}

/** Replaces the previous `{label}` token when the consent link label changes. */
export function syncConsentLabelInText(
  consentText: string,
  previousLabel: string,
  nextLabel: string
): string {
  const prev = previousLabel.trim();
  const next = nextLabel.trim();
  if (!prev) {
    return consentText;
  }

  const prevToken = `{${prev}}`;
  if (consentText.includes(prevToken)) {
    return next ? consentText.split(prevToken).join(`{${next}}`) : consentText.split(prevToken).join('');
  }

  if (consentText.includes(prev)) {
    return next ? consentText.split(prev).join(`{${next}}`) : consentText.split(prev).join('');
  }

  return consentText;
}

/** Splits consent copy into plain text and link segments for preview/live rendering. */
export function buildConsentTextSegments(
  consentText: string,
  consentLabel: string,
  consentUrl: string
): ConsentTextSegment[] {
  const text = consentText.trim();
  const label = consentLabel.trim();
  const url = consentUrl.trim();
  if (!text) {
    return [];
  }

  const token = label ? `{${label}}` : '';
  if (token && text.includes(token)) {
    return splitConsentTextByToken(text, token, label, url);
  }

  if (label && text.includes(label)) {
    return splitConsentTextByToken(text, label, label, url);
  }

  return [{ type: 'text', value: text }];
}

function splitConsentTextByToken(
  text: string,
  token: string,
  linkLabel: string,
  url: string
): ConsentTextSegment[] {
  const parts = text.split(token);
  const segments: ConsentTextSegment[] = [];

  parts.forEach((part, index) => {
    if (part) {
      segments.push({ type: 'text', value: part });
    }
    if (index < parts.length - 1 && linkLabel) {
      segments.push({
        type: 'link',
        value: linkLabel,
        url: url || '#'
      });
    }
  });

  return segments.length ? segments : [{ type: 'text', value: text }];
}
